/* eslint-disable react-refresh/only-export-components --
 * Design-system primitives: this module intentionally co-locates the TOKENS
 * object and helpers with components. Fast Refresh granularity does not apply. */
import { motion } from 'motion/react'
import { createContext, useContext, type CSSProperties, type ReactNode } from 'react'
import { cn } from '../../lib/utils'

export const TOKENS = {
  cream: '#f5ede1',
  creamSoft: 'rgba(245, 237, 225, 0.72)',
  creamDim: 'rgba(245, 237, 225, 0.5)',
  creamFaint: 'rgba(245, 237, 225, 0.35)',
  creamHairline: 'rgba(245, 237, 225, 0.06)',
  amber: '#e8c5a0',
  amberLight: '#f5d4a8',
  amberGlow: 'rgba(232, 197, 160, 0.4)',
  amberEmphasis: 'rgba(232, 197, 160, 0.55)',
  terracotta: '#c66e4d',
  terracottaSoft: 'rgba(198, 110, 77, 0.16)',
  /** Terracotta for small text on a tinted dark surface; see --warn-terracotta-text. */
  terracottaText: '#dd8a68',
  warmDark: 'rgba(28, 24, 22, 0.88)',
  hairlineStrong:
    'linear-gradient(90deg, rgba(245,237,225,0.22), rgba(245,237,225,0.06) 70%, transparent)',
  hairlineSoft:
    'linear-gradient(90deg, rgba(245,237,225,0.12), rgba(245,237,225,0.04) 70%, transparent)',
  // ── Panel surface (see <Panel>) ──────────────────────────────────────────
  // A soft surface, not a hard card: the wash is low enough that the page
  // gradient still reads through. Ported verbatim from the redesign prototype.
  panelWash:
    'linear-gradient(158deg, rgba(245,237,225,0.075) 0%, rgba(245,237,225,0.030) 52%, rgba(245,237,225,0.014) 100%)',
  panelWashDark:
    'linear-gradient(158deg, rgba(26,20,19,0.46) 0%, rgba(26,20,19,0.26) 55%, rgba(26,20,19,0.14) 100%)',
  creamBorder: 'rgba(245, 237, 225, 0.15)',
  panelBorderDark: 'rgba(245, 237, 225, 0.09)',
  panelShadow:
    '0 28px 56px -30px rgba(20,14,12,0.6), inset 0 1px 0 rgba(245,237,225,0.07)',
} as const

interface SectionProps {
  index?: string
  eyebrow?: string
  title?: string
  subtitle?: ReactNode
  children: ReactNode
  glow?: boolean
  className?: string
  id?: string
}

export function Section({
  index,
  eyebrow,
  title,
  subtitle,
  children,
  glow = false,
  className = '',
  id,
}: SectionProps) {
  return (
    <section id={id} className={`relative py-10 lg:py-14 ${className}`}>
      {glow && (
        <div
          aria-hidden
          className="absolute -inset-x-8 -inset-y-12 -z-10 pointer-events-none"
          style={{
            background:
              'radial-gradient(60% 80% at 70% 40%, rgba(232,197,160,0.08), transparent 70%)',
          }}
        />
      )}

      {(eyebrow || title) && (
        <SectionHeader
          index={index}
          eyebrow={eyebrow}
          title={title}
          subtitle={subtitle}
        />
      )}

      {children}
    </section>
  )
}

export function SectionHeader({
  index,
  eyebrow,
  title,
  subtitle,
  titleAs = 'h2',
}: {
  index?: string
  eyebrow?: string
  title?: string
  subtitle?: ReactNode
  /** Heading level for `title`. Defaults to 'h2' — this header is a *section*
   *  heading almost everywhere. Pass 'h1' only where it IS the page title, so
   *  the page gets its one page-naming h1 without a duplicate sr-only copy
   *  announcing the same words at a second level. Builder is the only such
   *  call site today. */
  titleAs?: 'h1' | 'h2'
}) {
  // Safe to pick the tag with a ternary rather than `motion[titleAs]`:
  // framer-motion's DOM component proxy caches one component per tag key, so
  // `motion.h1`/`motion.h2` are each referentially stable across renders.
  // `Title` therefore doesn't change identity when `titleAs` doesn't change,
  // so the heading never remounts — which matters because a remount would
  // restart its `whileInView` animation below. `motion[titleAs]` would read
  // from the same stable cache too, so it isn't wrong today, but it invites
  // someone to index with an unvalidated string later, which the proxy does
  // not guard against.
  const Title = titleAs === 'h1' ? motion.h1 : motion.h2

  return (
    <div className="mb-8 lg:mb-12">
      {(index || eyebrow) && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 text-[10px] tracking-[0.32em] uppercase"
          style={{ fontFamily: 'var(--font-mono)' }}
        >
          {index && (
            <span style={{ color: 'var(--accent-amber)' }}>{index}</span>
          )}
          {index && eyebrow && (
            <span style={{ color: 'var(--text-cream-faint)' }}>/</span>
          )}
          {eyebrow && (
            <span style={{ color: 'var(--text-cream-dim)' }}>{eyebrow}</span>
          )}
        </motion.div>
      )}
      {title && (
        <Title
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.08 }}
          className="mt-3 leading-[0.96] tracking-tight"
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontSize: 'clamp(1.875rem, 3.6vw, 2.75rem)',
            letterSpacing: '-0.022em',
            color: 'var(--text-cream)',
          }}
        >
          {title}
        </Title>
      )}
      {subtitle && (
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.18 }}
          className="mt-3 max-w-[58ch] text-sm lg:text-base leading-relaxed"
          style={{ fontFamily: 'var(--font-body)', color: 'var(--text-cream-soft)' }}
        >
          {subtitle}
        </motion.div>
      )}
    </div>
  )
}

export function Hairline({
  delay = 0,
  variant = 'strong',
  className = '',
}: {
  delay?: number
  variant?: 'strong' | 'soft'
  className?: string
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scaleX: 0.2 }}
      whileInView={{ opacity: 1, scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay }}
      className={`h-px origin-left ${className}`}
      style={{
        background:
          variant === 'strong'
            ? 'var(--hairline-strong)'
            : 'var(--hairline-soft)',
      }}
    />
  )
}

export function DataPoint({
  value,
  label,
  sub,
  delay = 0,
  emphasis = 'default',
}: {
  value: ReactNode
  label: string
  sub?: ReactNode
  delay?: number
  emphasis?: 'default' | 'alert'
}) {
  const valueColor =
    emphasis === 'alert' ? 'var(--warn-terracotta)' : 'var(--text-cream)'
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className="flex flex-col"
    >
      <span
        className="text-3xl lg:text-4xl leading-none tracking-tight tabular-nums"
        style={{ fontFamily: 'var(--font-display)', fontWeight: 500, color: valueColor }}
      >
        {value}
      </span>
      <span
        className="mt-3 text-[10px] tracking-[0.28em] uppercase"
        style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-cream-dim)' }}
      >
        {label}
      </span>
      {sub && (
        <span
          className="mt-1 text-xs"
          style={{ fontFamily: 'var(--font-body)', color: 'var(--text-cream-faint)' }}
        >
          {sub}
        </span>
      )}
    </motion.div>
  )
}

export function Chip({
  children,
  variant = 'default',
  title,
}: {
  children: ReactNode
  variant?: 'default' | 'amber' | 'warn' | 'ghost'
  title?: string
}) {
  const styles: Record<typeof variant, { bg: string; color: string; border: string }> = {
    default: {
      bg: 'rgba(245, 237, 225, 0.06)',
      color: 'var(--text-cream-soft)',
      border: 'rgba(245, 237, 225, 0.12)',
    },
    amber: {
      bg: 'rgba(232, 197, 160, 0.12)',
      color: 'var(--accent-amber)',
      border: 'rgba(232, 197, 160, 0.28)',
    },
    warn: {
      bg: 'var(--warn-terracotta-soft)',
      color: 'var(--warn-terracotta)',
      border: 'rgba(198, 110, 77, 0.32)',
    },
    ghost: {
      bg: 'transparent',
      color: 'var(--text-cream-dim)',
      border: 'rgba(245, 237, 225, 0.16)',
    },
  }
  const s = styles[variant]
  return (
    <span
      title={title}
      className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase border"
      style={{
        backgroundColor: s.bg,
        color: s.color,
        borderColor: s.border,
        fontFamily: 'var(--font-mono)',
      }}
    >
      {children}
    </span>
  )
}

export function Numeral({
  n,
  emphasis = false,
}: {
  n: number | string
  emphasis?: boolean
}) {
  return (
    <span
      className="inline-block tabular-nums leading-none"
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: '1.05rem',
        fontWeight: 500,
        color: emphasis ? 'var(--accent-amber)' : 'var(--text-cream-faint)',
        letterSpacing: '-0.02em',
      }}
    >
      {typeof n === 'number' ? String(n).padStart(2, '0') : n}
    </span>
  )
}

export function NumberedItem({
  n,
  title,
  children,
  delay = 0,
}: {
  n: number
  title?: string
  children: ReactNode
  delay?: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className="grid grid-cols-[2rem_1fr] gap-x-5 items-baseline"
    >
      <Numeral n={n} emphasis />
      <div>
        {title && (
          <h3
            className="text-base lg:text-lg leading-tight"
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 500,
              color: 'var(--text-cream)',
              letterSpacing: '-0.01em',
            }}
          >
            {title}
          </h3>
        )}
        <div
          className="mt-1.5 text-sm leading-relaxed"
          style={{ fontFamily: 'var(--font-body)', color: 'var(--text-cream-soft)' }}
        >
          {children}
        </div>
      </div>
    </motion.div>
  )
}

// Nested panels are banned by the shared design laws (a panel inside a panel
// reads as the identical-card-grid the No-Line rule exists to prevent). This
// context lets a Panel detect it is being rendered inside another one.
const InsidePanel = createContext(false)

export function Panel({
  variant = 'light',
  children,
  className = '',
  style,
}: {
  variant?: 'light' | 'dark'
  children: ReactNode
  className?: string
  style?: CSSProperties
}) {
  const nested = useContext(InsidePanel)
  if (nested && import.meta.env.DEV) {
    // Loud in development, silent in production — never white-screen a user
    // over a design-law violation, but make it impossible to miss while building.
    throw new Error(
      'Panel cannot be nested inside another Panel. Self-contained readouts get ' +
        'a surface; the editorial spine stays on the canvas.',
    )
  }
  const dark = variant === 'dark'
  return (
    <InsidePanel.Provider value={true}>
      <div
        className={cn('relative rounded-[20px] p-5 sm:rounded-3xl sm:px-7 sm:py-6.5', className)}
        style={{
          background: dark ? TOKENS.panelWashDark : TOKENS.panelWash,
          border: `1px solid ${dark ? TOKENS.panelBorderDark : TOKENS.creamBorder}`,
          boxShadow: TOKENS.panelShadow,
          ...style,
        }}
      >
        {children}
      </div>
    </InsidePanel.Provider>
  )
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span
      className="text-[10px] tracking-[0.28em] uppercase"
      style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-cream-dim)' }}
    >
      {children}
    </span>
  )
}
